import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Table, Input } from "reactstrap";
import { useLightTheme } from "./reportScheduleTheme";
import { getReportSurfaceColors } from "./reportDetailTheme";

const ReportPermissions = ({ activeColor = "blue", isTheme2 = false, permissions = [], setPermissionsData }) => {
  const { t } = useTranslation();
  const isLightTheme = useLightTheme();
  const surface = useMemo(
    () => getReportSurfaceColors(isLightTheme, isTheme2),
    [isLightTheme, isTheme2]
  );

  const tableData = useMemo(() => {
    return permissions || [];
  }, [permissions]);

  const handleToggle = (userId, field) => {
    if (!setPermissionsData) return;

    setPermissionsData(prev => prev.map(user => {
      if (user.id === userId) {
        const currentField = user[field];
        if (typeof currentField === 'object' && currentField !== null) {
          return {
            ...user,
            [field]: {
              ...currentField,
              value: !currentField.value
            }
          };
        }
      }
      return user;
    }));
  };

  const renderPermissionCell = (user, field) => {
    if (user.is_report_admin) return "User Administrator";

    const data = user[field];
    const isObject = typeof data === 'object' && data !== null;
    const label = isObject ? data.label : data;
    const value = isObject ? data.value : false;

    if (label === 'can_all') {
      return field === 'custom_report_edit' ? "Can Edit All Reports" : t('userDetail.generalInfo.reportAdmin.options.can_all');
    }

    if (label === 'can_assigned') {
      return (
        <div className="d-flex align-items-center">
          <Input
            type="checkbox"
            checked={!!value}
            onChange={() => handleToggle(user.id, field)}
            style={{
              margin: 0,
              position: 'relative',
              cursor: 'pointer',
              width: '18px',
              height: '18px'
            }}
          />
          <span className="ml-2">
            {field === 'custom_report_edit' ? "Can Edit Assigned Reports" : t('userDetail.generalInfo.reportAdmin.options.can_assigned')}
          </span>
        </div>
      );
    }

    if (label === 'cannot') {
      if (field === 'custom_report_access') return "Cannot Execute Custom Reports";
      return "Cannot Edit Reports";
    }

    if (field === 'standart_report_access' && typeof data === 'string') {
      if (data === 'can_all') return t('userDetail.generalInfo.reportAdmin.options.can_all');
      if (data === 'can_assigned') return t('userDetail.generalInfo.reportAdmin.options.can_assigned');
      return t('userDetail.generalInfo.reportAdmin.options.cannot');
    }

    return "-";
  };

  const headerCellStyle = {
    textTransform: 'none',
    color: surface.headerMuted,
    fontWeight: '400',
    fontSize: '13px',
    borderTop: 'none',
  };

  const bodyCellStyle = {
    fontSize: '14px',
    borderTop: 'none',
    padding: '12px 0',
    color: surface.bodyText,
  };

  return (
    <Card style={isTheme2 ? { backgroundColor: surface.panelBg, border: `1px solid ${surface.border}` } : undefined}>
      <CardBody>
        <Table responsive className="mt-2 text-left">
          <thead style={{ borderBottom: `1px solid ${surface.rowBorder}` }}>
            <tr>
              <th style={headerCellStyle}>
                User Name <i className="fas fa-arrow-down ml-1" style={{ fontSize: '10px' }} />
              </th>
              <th style={headerCellStyle}>
                Can Execute
              </th>
              <th style={headerCellStyle}>
                Can Edit
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? tableData.map((user) => (
              <tr key={user.id} style={{ borderBottom: `1px solid ${surface.rowBorder}` }}>
                <td style={bodyCellStyle}>
                  {user.full_name || user.user_detail?.full_name}
                </td>
                <td style={bodyCellStyle}>
                  {renderPermissionCell(user, 'custom_report_access')}
                </td>
                <td style={bodyCellStyle}>
                  {renderPermissionCell(user, 'custom_report_edit')}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="3" className="text-center py-4" style={{ color: surface.headerMuted }}>
                  No permissions defined.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
};

export default ReportPermissions;
